import { useFillPack } from "./FillPackContext";
import type {
    FillPackPlacementMode,
    FillPackRotationMode,
} from "../../utils/fillPackPlacement";
import type { PathPopulateGradientMode } from "./PathPopulateContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { useEffect, useRef } from "react";
import {
    FileUp,
    Grid3x3,
    LayoutGrid,
    Palette,
    Plus,
    Share2,
    Shapes,
    Trash2,
} from "lucide-react";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import {
    fileNameWithFlattenSuffix,
    flattenSvgToRootPaths,
} from "../../utils/flattenSvgGeometry";
import { isValidSvg } from "../../utils/svgUtils";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function FillPackControls() {
    const {
        objectSvgs,
        pathSvg,
        spacing,
        inset,
        scale,
        error,
        rotationMode,
        baseRotationDeg,
        anchor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        maxGradientSteps,
        objectPlacementMode,
        cellSpanMin,
        cellSpanMax,
        setCellSpanMin,
        setCellSpanMax,
        showPathGuides,
        pathGuideStroke,
        pathGuideStrokeWidth,
        flattenOnImport,
        explicitFillOnly,
        packRegionEnabled,
        packRegionMinX,
        packRegionMinY,
        packRegionMaxX,
        packRegionMaxY,
        setFlattenOnImport,
        setExplicitFillOnly,
        setPackRegionEnabled,
        setPackRegionMinX,
        setPackRegionMinY,
        setPackRegionMaxX,
        setPackRegionMaxY,
        resetPackRegionToViewBox,
        supportsFillHitTest,
        setSpacing,
        setInset,
        setScale,
        setRotationMode,
        setBaseRotationDeg,
        setAnchor,
        setGradientEnabled,
        setGradientMode,
        setGradientColors,
        setMaxGradientSteps,
        setObjectPlacementMode,
        setShowPathGuides,
        setPathGuideStroke,
        setPathGuideStrokeWidth,
        objectInputRefs,
        pathInputRef,
        handleFileLoad,
        addObjectSvg,
        removeObjectSvg,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useFillPack();

    const flattenOnImportRef = useRef(flattenOnImport);
    flattenOnImportRef.current = flattenOnImport;

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                const flat = flattenOnImportRef.current
                    ? flattenSvgToRootPaths(text)
                    : null;
                addObjectSvg({
                    name: fileNameWithFlattenSuffix(file.name, Boolean(flat)),
                    content: flat ?? text,
                });
            };
            reader.readAsText(file);
        }
    };

    const minPaletteColors =
        gradientMode === "random-per-path" ||
        gradientMode === "random-palette-per-instance"
            ? 1
            : 2;

    const handleAddColor = () => {
        setGradientColors([...gradientColors, "#000000"]);
    };

    const handleRemoveColor = (index: number) => {
        if (gradientColors.length > minPaletteColors) {
            setGradientColors(gradientColors.filter((_, i) => i !== index));
        }
    };

    const handleMoveColorUp = (index: number) => {
        if (index > 0) {
            const next = [...gradientColors];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            setGradientColors(next);
        }
    };

    const handleMoveColorDown = (index: number) => {
        if (index < gradientColors.length - 1) {
            const next = [...gradientColors];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            setGradientColors(next);
        }
    };

    const handleColorChange = (index: number, color: string) => {
        const next = [...gradientColors];
        next[index] = color;
        setGradientColors(next);
    };

    const colorTimeoutRefs = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );

    useEffect(() => {
        return () => {
            colorTimeoutRefs.current.forEach(clearTimeout);
            colorTimeoutRefs.current.clear();
        };
    }, []);

    const handleColorPickerChange = (index: number, color: string) => {
        const existing = colorTimeoutRefs.current.get(index);
        if (existing) clearTimeout(existing);
        const t = setTimeout(() => {
            handleColorChange(index, color);
            colorTimeoutRefs.current.delete(index);
        }, 300);
        colorTimeoutRefs.current.set(index, t);
    };

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Object Fill Pack</h2>
                    <ToolDescriptionPopover dialogLabel="Object Fill Pack tool description">
                        <p>
                            Load <strong>object SVGs</strong> and a{" "}
                            <strong>path SVG</strong>. Closed shapes that have a
                            visible fill are packed on a grid inside their
                            outlines. Export replaces fills with the packed
                            instances; optional stroke guides keep the original
                            silhouette.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="object-fill-pack"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                {!supportsFillHitTest && (
                    <p className="fill-pack-warning">
                        Fill hit-testing is unavailable in this browser; packing
                        may produce no instances.
                    </p>
                )}

                <CollapsibleToolGroup title="Object SVGs" icon={Shapes}>
                    <label className="field-label">Object SVGs</label>
                    <p className="path-populate-preset-hint">Quick add preset</p>
                    <div className="path-populate-preset-chips">
                        {OBJECT_SVG_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                                title={`Add ${preset.label} as object`}
                                aria-label={`Add ${preset.label} as object`}
                                onClick={() => {
                                    if (!isValidSvg(preset.svg)) return;
                                    addObjectSvg({
                                        name: `${preset.label} (preset)`,
                                        content: preset.svg,
                                    });
                                }}
                            >
                                <span
                                    className="path-populate-preset-chip-icon"
                                    aria-hidden="true"
                                    dangerouslySetInnerHTML={{
                                        __html: preset.svg,
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                    <div className="path-populate-object-rows">
                        {objectSvgs.map((svg, index) => (
                            <div
                                key={index}
                                className="path-populate-object-row"
                            >
                                <div className="upload-controls">
                                    <button
                                        type="button"
                                        className="tool-file-btn tool-file-btn--stretch"
                                        onClick={() => {
                                            objectInputRefs.current?.[
                                                index
                                            ]?.click();
                                        }}
                                    >
                                        <FileUp
                                            className="tool-file-btn__icon"
                                            size={18}
                                            aria-hidden
                                        />
                                        <span>
                                            {svg.name ||
                                                `Choose object SVG ${index + 1}…`}
                                        </span>
                                    </button>
                                    <input
                                        ref={(el) => {
                                            if (el) {
                                                objectInputRefs.current[index] =
                                                    el;
                                            }
                                        }}
                                        type="file"
                                        accept=".svg,image/svg+xml"
                                        className="upload-input"
                                        onChange={handleFileLoad(
                                            "object",
                                            index,
                                        )}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="tool-action-btn tool-action-btn--danger"
                                    onClick={() => removeObjectSvg(index)}
                                >
                                    <Trash2
                                        className="tool-action-btn__icon"
                                        size={16}
                                        aria-hidden
                                    />
                                    <span>Remove</span>
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--add"
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.accept = ".svg,image/svg+xml";
                                input.style.display = "none";
                                input.addEventListener("change", (e) => {
                                    importObjectFiles(
                                        (e.target as HTMLInputElement).files ?? [],
                                    );
                                    document.body.removeChild(input);
                                });
                                document.body.appendChild(input);
                                input.click();
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importObjectFiles(e.dataTransfer.files);
                            }}
                        >
                            <Plus
                                className="tool-action-btn__icon"
                                size={16}
                                aria-hidden
                            />
                            <span>Add object SVG</span>
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Path SVG" icon={LayoutGrid}>
                    <CheckboxField
                        inline
                        checked={flattenOnImport}
                        onChange={setFlattenOnImport}
                        title="Bakes group transforms into plain paths with solid fills (similar to Figma flatten). Keeps the file viewBox. Use for nested-grid style SVGs when many group transforms should become root-level geometry."
                        label="Flatten SVG on import"
                    />
                    <CheckboxField
                        inline
                        checked={explicitFillOnly}
                        onChange={setExplicitFillOnly}
                        title="Only include closed shapes that declare a non-none fill in the SVG/style chain. Useful to skip full-canvas helper rects that rely on default fill."
                        label="Only use explicitly filled shapes"
                    />
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="tool-file-btn tool-file-btn--stretch"
                            onClick={() => pathInputRef.current?.click()}
                        >
                            <FileUp
                                className="tool-file-btn__icon"
                                size={18}
                                aria-hidden
                            />
                            <span>
                                {pathSvg?.name ?? "Choose path SVG…"}
                            </span>
                        </button>
                        <input
                            ref={pathInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleFileLoad("path")}
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Pack grid" icon={Grid3x3}>
                    <div className="field-row">
                        <label className="field-label">
                            Spacing (SVG units)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={spacing}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 1 &&
                                        n <= 5000
                                    ) {
                                        setSpacing(n);
                                    }
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Inset from bbox (SVG units)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={inset}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 0 &&
                                        n <= 2000
                                    ) {
                                        setInset(n);
                                    }
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Object scale (% of larger bbox side)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={scale}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 1 &&
                                        n <= 500
                                    ) {
                                        setScale(n);
                                    }
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Cell span min
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={20}
                                step={1}
                                value={cellSpanMin}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 1 &&
                                        n <= 20
                                    ) {
                                        setCellSpanMin(n);
                                    }
                                }}
                            />
                        </label>
                        <label className="field-label">
                            Cell span max
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={20}
                                step={1}
                                value={cellSpanMax}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 1 &&
                                        n <= 20
                                    ) {
                                        setCellSpanMax(n);
                                    }
                                }}
                            />
                        </label>
                    </div>
                    <p className="path-populate-preset-hint">
                        Span s scales the object by s× and reserves an s×s block
                        of grid cells (no overlap). Span is hashed per anchor
                        cell then reduced if the block is not fully inside the
                        fill or overlaps an earlier block. Min=max=1 matches the
                        old dense pack.
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Anchor
                            <select
                                value={anchor}
                                onChange={(e) => setAnchor(e.target.value)}
                                className="anchor-select"
                            >
                                <option value="center">Center</option>
                                <option value="top-left">Top left</option>
                                <option value="top">Top</option>
                                <option value="top-right">Top right</option>
                                <option value="center-left">Center left</option>
                                <option value="center-right">
                                    Center right
                                </option>
                                <option value="bottom-left">Bottom left</option>
                                <option value="bottom">Bottom</option>
                                <option value="bottom-right">
                                    Bottom right
                                </option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rotation
                            <select
                                value={rotationMode}
                                onChange={(e) =>
                                    setRotationMode(
                                        e.target
                                            .value as FillPackRotationMode,
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="align">Upright (0° + offset)</option>
                                <option value="random">Random per instance</option>
                                <option value="none">Fixed offset only</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Base rotation (degrees)
                            <DebouncedInput
                                type="number"
                                min={-360}
                                max={360}
                                step={1}
                                value={baseRotationDeg}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number"
                                            ? v
                                            : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= -360 &&
                                        n <= 360
                                    ) {
                                        setBaseRotationDeg(n);
                                    }
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Object choice
                            <select
                                value={objectPlacementMode}
                                onChange={(e) =>
                                    setObjectPlacementMode(
                                        e.target
                                            .value as FillPackPlacementMode,
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="alternating">Alternating</option>
                                <option value="random">Random</option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Pack region" icon={Grid3x3}>
                    <CheckboxField
                        inline
                        checked={packRegionEnabled}
                        onChange={setPackRegionEnabled}
                        title="Only keep placements whose cell block lies fully inside this rectangle (viewBox units). Useful to exclude parts of a silhouette such as a baked-in ground band."
                        label="Limit packing to rectangle"
                    />
                    {packRegionEnabled && (
                        <>
                            <p className="path-populate-preset-hint">
                                Coordinates are in the path SVG's viewBox units.
                            </p>
                            <div className="field-row field-row--two">
                                <label className="field-label">
                                    Min X
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={packRegionMinX}
                                        onChange={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            if (Number.isFinite(n)) {
                                                setPackRegionMinX(n);
                                            }
                                        }}
                                    />
                                </label>
                                <label className="field-label">
                                    Min Y
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={packRegionMinY}
                                        onChange={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            if (Number.isFinite(n)) {
                                                setPackRegionMinY(n);
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                            <div className="field-row field-row--two">
                                <label className="field-label">
                                    Max X
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={packRegionMaxX}
                                        onChange={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            if (Number.isFinite(n)) {
                                                setPackRegionMaxX(n);
                                            }
                                        }}
                                    />
                                </label>
                                <label className="field-label">
                                    Max Y
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={packRegionMaxY}
                                        onChange={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            if (Number.isFinite(n)) {
                                                setPackRegionMaxY(n);
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={resetPackRegionToViewBox}
                            >
                                Reset to viewBox
                            </button>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Path guides" icon={LayoutGrid}>
                    <div className="field-row">
                        <CheckboxField
                            checked={showPathGuides}
                            onChange={setShowPathGuides}
                            label="Show stroke-only guides (preview & export)"
                            inline
                        />
                    </div>
                    {showPathGuides && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Guide stroke
                                    <input
                                        type="color"
                                        value={
                                            /^#[0-9a-fA-F]{6}$/.test(
                                                pathGuideStroke,
                                            )
                                                ? pathGuideStroke
                                                : "#888888"
                                        }
                                        onChange={(e) =>
                                            setPathGuideStroke(e.target.value)
                                        }
                                        style={{
                                            width: "50px",
                                            height: "30px",
                                        }}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Guide stroke width
                                    <DebouncedInput
                                        type="number"
                                        min={0.1}
                                        max={20}
                                        step={0.1}
                                        value={pathGuideStrokeWidth}
                                        onChange={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            if (
                                                Number.isFinite(n) &&
                                                n >= 0.1 &&
                                                n <= 20
                                            ) {
                                                setPathGuideStrokeWidth(n);
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Color" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            checked={gradientEnabled}
                            onChange={setGradientEnabled}
                            label="Apply color to objects"
                            inline
                        />
                    </div>
                    {gradientEnabled && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Color mode
                                    <select
                                        value={gradientMode}
                                        onChange={(e) =>
                                            setGradientMode(
                                                e.target
                                                    .value as PathPopulateGradientMode,
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="per-path">
                                            Gradient (per region)
                                        </option>
                                        <option value="global">
                                            Gradient (global)
                                        </option>
                                        <option value="random-per-path">
                                            Random solid per region
                                        </option>
                                        <option value="random-palette-per-instance">
                                            Random from palette per instance
                                        </option>
                                        <option value="random-per-instance">
                                            Random color per instance
                                        </option>
                                    </select>
                                </label>
                            </div>
                            {gradientMode !== "random-per-instance" && (
                                <div className="field-row column">
                                    <label className="field-label">
                                        {gradientMode === "random-per-path" ||
                                        gradientMode ===
                                            "random-palette-per-instance"
                                            ? "Palette colors"
                                            : "Gradient colors"}
                                    </label>
                                    <div className="path-populate-gradient-colors">
                                        {gradientColors.map((color, index) => (
                                            <div
                                                key={index}
                                                className="path-populate-gradient-row"
                                            >
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleMoveColorUp(index)
                                                    }
                                                    disabled={index === 0}
                                                    title="Move up"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleMoveColorDown(
                                                            index,
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        gradientColors.length - 1
                                                    }
                                                    title="Move down"
                                                >
                                                    ↓
                                                </button>
                                                <input
                                                    type="color"
                                                    value={color}
                                                    onChange={(e) =>
                                                        handleColorPickerChange(
                                                            index,
                                                            e.target.value,
                                                        )
                                                    }
                                                    style={{
                                                        width: "50px",
                                                        height: "30px",
                                                        minWidth: "30px",
                                                    }}
                                                />
                                                <DebouncedInput
                                                    type="text"
                                                    value={color}
                                                    onChange={(value) =>
                                                        handleColorChange(
                                                            index,
                                                            String(value),
                                                        )
                                                    }
                                                    style={{
                                                        flex: 1,
                                                        padding: "4px",
                                                        fontFamily: "monospace",
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleRemoveColor(index)
                                                    }
                                                    disabled={
                                                        gradientColors.length <=
                                                        minPaletteColors
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="path-populate-inline-btn"
                                            onClick={handleAddColor}
                                        >
                                            Add color
                                        </button>
                                    </div>
                                </div>
                            )}
                            {gradientMode !== "random-per-path" &&
                                gradientMode !== "random-per-instance" &&
                                gradientMode !==
                                    "random-palette-per-instance" && (
                                    <div className="field-row">
                                        <label className="field-label">
                                            Max gradient steps
                                            <DebouncedInput
                                                type="number"
                                                min={2}
                                                max={100}
                                                step={1}
                                                value={maxGradientSteps}
                                                onChange={(v) => {
                                                    const n =
                                                        typeof v === "number"
                                                            ? v
                                                            : Number(v);
                                                    if (
                                                        Number.isFinite(n) &&
                                                        n >= 2 &&
                                                        n <= 100
                                                    ) {
                                                        setMaxGradientSteps(n);
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                )}
                        </>
                    )}
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "fill-pack-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["object-fill-pack"]}
                    />
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
